import { Link } from "react-router-dom";
import Disclaimer from "../components/Disclaimer";
import { useLanguage } from "../LanguageContext";

const BANNER_ITEMS = [
  { icon: "🌍", text: "Available in 4 Languages" },
  { icon: "🔒", text: "Private & Secure — Files Never Stored" },
  { icon: "⚡", text: "AI Results in Seconds" },
  { icon: "🩺", text: "Trusted Health Screening" },
];

function MarqueeBanner() {
  // Duplicate items so the loop is seamless
  const items = [...BANNER_ITEMS, ...BANNER_ITEMS];
  return (
    <div className="bg-stone-800 text-stone-200 overflow-hidden py-2.5 select-none">
      <div className="flex animate-marquee whitespace-nowrap">
        {items.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-2 mx-8 text-sm font-medium flex-shrink-0">
            <span className="text-base">{item.icon}</span>
            {item.text}
            <span className="text-stone-500 ml-6">•</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, to, badge, tryItNow }) {
  return (
    <Link
      to={to}
      className="group bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
          {icon}
        </div>
        {badge && (
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div>
        <h3 className="font-semibold text-stone-900 text-lg mb-1">{title}</h3>
        <p className="text-stone-500 text-sm leading-relaxed">{description}</p>
      </div>
      <div className="mt-auto flex items-center gap-1 text-amber-700 text-sm font-medium">
        {tryItNow}
        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 rounded-full bg-amber-700 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-stone-900 mb-1">{title}</h4>
        <p className="text-stone-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useLanguage();
  const h = t.home;

  return (
    <div>
      {/* Scrolling banner */}
      <MarqueeBanner />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-stone-100">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-stone-50 opacity-70" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">

          <div
            className="animate-fade-in-up inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full mb-6"
            style={{ animationDelay: "0.05s" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            {h.badge}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 tracking-tight leading-tight mb-6">
            <span
              className="animate-fade-in-up block"
              style={{ animationDelay: "0.25s" }}
            >
              {h.headline1}
            </span>
            <span
              className="animate-fade-in-up block text-amber-700"
              style={{ animationDelay: "0.6s" }}
            >
              {h.headline2}
            </span>
          </h1>

          <p
            className="animate-fade-in-up text-lg sm:text-xl text-stone-500 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ animationDelay: "1.0s" }}
          >
            {h.subtitle}
          </p>

          <div
            className="animate-fade-in-up flex flex-col sm:flex-row items-center justify-center gap-3"
            style={{ animationDelay: "1.35s" }}
          >
            <Link
              to="/voice"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              {h.analyzeVoice}
            </Link>
            <Link
              to="/skin"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white border border-stone-300 hover:border-amber-400 text-stone-700 hover:text-amber-800 font-semibold px-6 py-3 rounded-xl transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {h.analyzeSkin}
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 space-y-16">

        <section>
          <h2 className="text-2xl font-bold text-stone-900 mb-2">{h.whatCanScreen}</h2>
          <p className="text-stone-500 mb-8 text-sm">{h.uploadSubtitle}</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <FeatureCard
              to="/voice" badge={h.voiceBadge}
              title={h.voiceCardTitle} description={h.voiceCardDesc}
              tryItNow={h.tryItNow}
              icon={
                <svg className="w-6 h-6 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              }
            />
            <FeatureCard
              to="/skin" badge={h.skinBadge}
              title={h.skinCardTitle} description={h.skinCardDesc}
              tryItNow={h.tryItNow}
              icon={
                <svg className="w-6 h-6 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              }
            />
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-8">{h.howItWorks}</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            <Step number="1" title={h.step1Title} description={h.step1Desc} />
            <Step number="2" title={h.step2Title} description={h.step2Desc} />
            <Step number="3" title={h.step3Title} description={h.step3Desc} />
          </div>
        </section>

        <section className="grid sm:grid-cols-3 gap-5 text-center">
          {[
            { icon: "🔒", title: h.privateTitle,     desc: h.privateDesc },
            { icon: "🤖", title: h.aiTitle,          desc: h.aiDesc },
            { icon: "📋", title: h.transparentTitle, desc: h.transparentDesc },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
              <div className="text-3xl mb-3">{icon}</div>
              <h4 className="font-semibold text-stone-900 mb-1">{title}</h4>
              <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </section>

        <Disclaimer />
      </div>
    </div>
  );
}
